import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isMacOS } from "../../../lib/platform.ts";
import { ArrowsPointingInIcon, PresentationChartBarIcon } from "@heroicons/react/24/outline";
import { useQuinnFullscreenView } from "../QuinnContext.tsx";
import { QuinnCopy, QUINN_OVERLAY_BREAKPOINT_PX } from "../types";
import { isServerChatId, QUINN_FULLSCREEN_PATH, quinnFullscreenPath } from "../utils";
import { QuinnHistorySidebar } from "../components/History";
import { QuinnThread } from "../components/Thread";
import { QuinnComposer } from "../components/Composer";
import { QuinnArtifactCanvasContext, QuinnCanvas } from "../components/Canvas";
import { useQuinnCanvasState } from "../hooks/useQuinnCanvasState.ts";

const CONTENT_COLUMN = "mx-auto w-full max-w-3xl";

export function QuinnFullscreenPage() {
    const quinn = useQuinnFullscreenView();
    const { chatId: routeChatId } = useParams<{ chatId?: string }>();
    const navigate = useNavigate();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
        () => window.innerWidth < QUINN_OVERLAY_BREAKPOINT_PX,
    );
    const handledRef = useRef<{ routeChatId: string | undefined; chatId: string | null } | null>(null);

    const { chatId, isHydrating, openChat, startNewChat } = quinn;
    const location = useLocation();
    const requestedArtifactId = (location.state as { openArtifactId?: string } | null)?.openArtifactId ?? null;
    const canvasState = useQuinnCanvasState(chatId, quinn.messages, requestedArtifactId);
    const canvasToggleLabel = canvasState.isOpen ? QuinnCopy.HideCanvas : QuinnCopy.ShowCanvas;

    useEffect(() => {
        const handled = handledRef.current;
        handledRef.current = { routeChatId, chatId };

        if (handled !== null && handled.routeChatId === routeChatId) {
            if (handled.chatId !== chatId && (chatId ?? undefined) !== routeChatId) {
                navigate(quinnFullscreenPath(chatId), { replace: true });
            }
            return;
        }

        const fallBackToNewChat = () => {
            startNewChat();
            navigate(QUINN_FULLSCREEN_PATH, { replace: true });
        };

        if (routeChatId === undefined) {
            if (handled === null) {
                if (chatId !== null) navigate(quinnFullscreenPath(chatId), { replace: true });
            } else if (chatId !== null || isHydrating) {
                startNewChat();
            }
            return;
        }
        if (routeChatId === chatId && !isHydrating) return;
        if (!isServerChatId(routeChatId)) {
            fallBackToNewChat();
            return;
        }

        void openChat(routeChatId).then((result) => {
            if (result === "not_found" && handledRef.current?.routeChatId === routeChatId) fallBackToNewChat();
        });
    }, [chatId, isHydrating, navigate, openChat, routeChatId, startNewChat]);

    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            const modifier = isMacOS() ? event.metaKey : event.ctrlKey;
            if (modifier && event.shiftKey && event.key.toLowerCase() === "o") {
                event.preventDefault();
                startNewChat();
            }
        }

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [startNewChat]);

    const activeChat = quinn.chats.find((chat) => chat.id === chatId);
    const title = activeChat?.title ?? (quinn.messages.length > 0 ? QuinnCopy.CurrentChat : QuinnCopy.NewChat);

    return (
        <QuinnArtifactCanvasContext.Provider value={canvasState.canvas}>
            <div className="relative flex h-full bg-white text-gray-900">
                <QuinnHistorySidebar
                    chats={quinn.chats}
                    activeChatId={chatId}
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapsed={() => setIsSidebarCollapsed((current) => !current)}
                    onOpenChat={(id) => navigate(quinnFullscreenPath(id))}
                    onNewChat={startNewChat}
                    onBack={() => quinn.exitFullscreen("page")}
                />

                <section className="flex min-w-0 flex-1 flex-col">
                    <header className="flex items-center gap-3 border-b border-gray-200 px-6 py-3">
                        <div className="min-w-0 flex-1">
                            <h1 className="truncate text-sm font-semibold text-gray-900">{title}</h1>
                            <p className="truncate text-xs text-gray-500">
                                {QuinnCopy.Quinn} · {quinn.scope.businessLabel}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={canvasState.toggle}
                            disabled={canvasState.artifacts.length === 0}
                            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label={canvasToggleLabel}
                            title={canvasToggleLabel}
                        >
                            <PresentationChartBarIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => quinn.exitFullscreen("drawer")}
                            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                            aria-label={QuinnCopy.ShrinkToDrawer}
                            title={QuinnCopy.ShrinkToDrawer}
                        >
                            <ArrowsPointingInIcon className="h-5 w-5" />
                        </button>
                    </header>

                    <QuinnThread
                        messages={quinn.messages}
                        isBusy={quinn.isBusy}
                        isHydrating={quinn.isHydrating}
                        pageLabel={quinn.pageContext.label}
                        suggestions={quinn.suggestions}
                        onPickSuggestion={(suggestion) => quinn.send(suggestion)}
                        onRetry={(message) => quinn.retry(message.id)}
                        onAnswerPrompt={quinn.answerPrompt}
                        onRate={quinn.rateAnswer}
                        contentClassName={CONTENT_COLUMN}
                    />

                    <div className={CONTENT_COLUMN}>
                        <QuinnComposer
                            canAskAllClients={quinn.canAskAllClients}
                            scope={quinn.scope}
                            businesses={quinn.businesses}
                            isBusy={quinn.isBusy}
                            isHydrating={quinn.isHydrating}
                            draft={quinn.draft}
                            onDraftChange={quinn.setDraft}
                            onScopeChange={quinn.setScope}
                            onSubmit={quinn.send}
                            onStop={quinn.stop}
                        />
                    </div>
                </section>

                {canvasState.isOpen && canvasState.activeArtifact && (
                    <QuinnCanvas
                        artifact={canvasState.activeArtifact}
                        isBusy={quinn.isBusy}
                        onAsk={(question) => {
                            const artifact = canvasState.activeArtifact!;
                            quinn.send(question, {
                                artifactId: artifact.id,
                                kind: artifact.kind,
                                title: artifact.title,
                                clientBusinessId: artifact.clientBusinessId,
                                drawnForAllClients: artifact.drawnForAllClients,
                            });
                        }}
                        onClose={canvasState.close}
                        className={
                            quinn.isOverlay
                                ? "absolute inset-y-0 right-0 z-20 w-[min(100%,36rem)] shadow-xl"
                                : "w-[45%] min-w-[420px] shrink-0"
                        }
                    />
                )}
            </div>
        </QuinnArtifactCanvasContext.Provider>
    );
}
